import React from "react";
import { Page, Text, View, Document, Image } from "@react-pdf/renderer";
import {
  styles,
  narrowCells,
  CellTextBox,
  NumberAsPercent,
  NumberAsTwoDigits,
  safeString,
  safeZero,
} from "./reportingHelpers";
import { parseISO8601 } from "./iso8601";
import { IntlProvider } from "react-intl";
import {
  FixtureReportsHeader,
  ScoringTable,
  parseScoringIntervals,
} from "./FixtureReportsHeader";
import { Legend } from "./BoxScoreLegend";
import { FixtureReportsFooter } from "./FixtureReportsFooter";
import { getPrimaryOfficials } from "./officialRoles.js";
import { AvailableOfficialsList } from "./AvailableOfficialsList.jsx";

const parsePeriodId = (periodId) => {
  if (periodId.startsWith("OT")) {
    const periodNumber = parseInt(periodId.slice(2));
    return 10 + periodNumber;
  } else {
    return parseInt(periodId.slice(1));
  }
};

const getPeriodIndexFromId = (periodId) => {
  if (periodId > 10) return 3 + periodId - 10;
  return periodId - 1;
};

const buildRosterPersonStats = (team) =>
  Object.entries(team?.team?.persons ?? {}).map(([personId, person]) => ({
    personId,
    personFullName: person.name ?? "",
    bib: person.bib,
    starter: person.starter,
    captain: person.captain,
    minutes: "PT0M",
    freeThrowsMade: 0,
    foulsTotal: 0,
    points: 0,
    efficiency: 0,
    pir: 0,
    indexOfSuccess: 0,
  }));

const normalizePersonStats = (team) => {
  const rosterPersonStats = buildRosterPersonStats(team);

  if (Array.isArray(team?.personStats) && team.personStats.length) {
    return team.personStats;
  }

  return rosterPersonStats;
};

const getBoxScoreTableLayout = (pointsFourAllowed) => {
  const shootingCell = pointsFourAllowed ? 7.8 : 9;
  const minutes = pointsFourAllowed ? 10 : 12;
  const shootingGroupCount = pointsFourAllowed ? 5 : 4;

  const shootingGroup = shootingCell * 2;
  const shootingColumns = minutes + shootingGroup * shootingGroupCount;

  const toMillimeters = (value) => `${Number(value.toFixed(1))}mm`;

  return {
    shootingCellWidth: toMillimeters(shootingCell),
    shootingGroupWidth: toMillimeters(shootingGroup),
    shootingColumnsWidth: toMillimeters(shootingColumns),
    minutesWidth: toMillimeters(minutes),
  };
};

export const BoxScoreReport = (props) => {
  const {
    homeTeam,
    awayTeam,
    fixture,
    scoringIntervals,
    roles,
    fixtureProfile: { profile: fixtureProfile },
  } = props.data;
  const { language, locale, intl, periodId: inPeriodId = "full" } = props;

  const periodId = inPeriodId !== "full" ? parsePeriodId(inPeriodId) : "full";

  let homeTeamPeriods,
    awayTeamPeriods = [];
  let periodsScores = [];

  if (homeTeam && awayTeam && scoringIntervals) {
    homeTeamPeriods = parseScoringIntervals(homeTeam, scoringIntervals, intl);
    awayTeamPeriods = parseScoringIntervals(awayTeam, scoringIntervals, intl);

    let lastHomeScore = 0,
      lastAwayScore = 0;
    homeTeamPeriods.forEach((period, index) => {
      const awayPeriod = awayTeamPeriods[index];
      const newHomeScore = period.interval2 || period.interval1;
      const newAwayScore = awayPeriod.interval2 || awayPeriod.interval1;
      periodsScores.push(
        `${newHomeScore - lastHomeScore}-${newAwayScore - lastAwayScore}`
      );
      lastHomeScore = newHomeScore;
      lastAwayScore = newAwayScore;
    });

    if (periodId !== "full") {
      homeTeamPeriods = homeTeamPeriods.filter((p) => p.periodId === periodId);
      awayTeamPeriods = awayTeamPeriods.filter((p) => p.periodId === periodId);
      periodsScores = periodsScores.splice(getPeriodIndexFromId(periodId), 1);
    }

    homeTeam["score"] = lastHomeScore;
    awayTeam["score"] = lastAwayScore;

    if (periodId !== "full") {
      homeTeam["personStats"] = homeTeam.personStatsPeriods.filter(
        (p) => p.periodId === periodId
      );
      awayTeam["personStats"] = awayTeam.personStatsPeriods.filter(
        (p) => p.periodId === periodId
      );
      homeTeam["teamStats"] = homeTeam.teamStatsPeriods.find(
        (p) => p.periodId === periodId
      );
      awayTeam["teamStats"] = awayTeam.teamStatsPeriods.find(
        (p) => p.periodId === periodId
      );
    }

    homeTeam["personStats"] = normalizePersonStats(homeTeam);
    awayTeam["personStats"] = normalizePersonStats(awayTeam);
    homeTeam.personStats.sort((a, b) => a.bib - b.bib);
    awayTeam.personStats.sort((a, b) => a.bib - b.bib);

    let title = intl.formatMessage({
      id: "reports.boxscore",
      defaultMessage: "Boxscore",
    });
    if (inPeriodId !== "full") title += ` - ${inPeriodId}`;

    const officialRoles = getPrimaryOfficials();
    const availableOfficials = (roles ?? [])
      .filter((person) => officialRoles.includes(person.role))
      .reduce((soFar, person) => {
        if (soFar[person.role]) {
          soFar[person.role].push(person);
          return soFar;
        }
        return {
          ...soFar,
          [person.role]: [person],
        };
      }, {});

    return (
      <IntlProvider locale={locale ?? "en-US"}>
        <Document>
          <Page size="A4" style={styles.page}>
            <BoxScoreReportPage
              {...{
                homeTeam,
                awayTeam,
                fixture,
                homeTeamPeriods,
                awayTeamPeriods,
                periodsScores,
                language,
                locale,
                intl,
                roles,
                title,
                fixtureProfile,
                availableOfficials,
              }}
            />
          </Page>
        </Document>
      </IntlProvider>
    );
  } else {
    return null;
  }
};

const BoxScoreReportPage = ({
  homeTeam,
  awayTeam,
  fixture,
  homeTeamPeriods,
  awayTeamPeriods,
  periodsScores,
  language,
  locale,
  intl,
  roles,
  title,
  fixtureProfile,
  availableOfficials,
}) => (
  <View>
    <FixtureReportsHeader
      {...{
        homeTeam,
        awayTeam,
        fixture,
        periodsScores,
        language,
        locale,
        title,
        intl,
        roles,
        reportGeneratedTitleHidden: true,
      }}
    />
    <ScoringTable
      homeTeam={homeTeam.team}
      awayTeam={awayTeam.team}
      {...{ homeTeamPeriods, awayTeamPeriods, intl, fixtureProfile, fixture }}
    />
    <AvailableOfficialsList
      availableOfficials={availableOfficials}
      intl={intl}
    ></AvailableOfficialsList>
    <TeamBoxscoreTable
      team={homeTeam}
      intl={intl}
      roles={roles}
      fixtureProfile={fixtureProfile}
    />
    <TeamBoxscoreTable
      team={awayTeam}
      intl={intl}
      roles={roles}
      fixtureProfile={fixtureProfile}
    />
    <View
      style={{
        ...styles.section,
        flexDirection: "row",
        justifyContent: "space-between",
        marginTop: "8pt",
      }}
    >
      <SideTableLeft {...{ homeTeam, awayTeam, intl }} />
      <SideTableRight {...{ homeTeam, awayTeam, intl }} />
    </View>
    <View
      style={{
        ...styles.section,
        border: 1,
      }}
    >
      <Legend {...{ intl }} />
    </View>
    <FixtureReportsFooter roles={roles} intl={intl} />
  </View>
);

const TeamBoxscoreTable = ({ team, intl, roles, fixtureProfile }) => {
  const pointsFourAllowed = fixtureProfile?.pointsFourAllowed === true;
  const tableLayout = getBoxScoreTableLayout(pointsFourAllowed);
  const coachName = roles.find(
    (r) => r.entityId === team.team.entityId && r.role === "COACH"
  )?.name;
  const assistantCoachesNames = roles
    .filter(
      (r) => r.entityId === team.team.entityId && r.role === "COACH_ASSISTANT"
    )
    .sort((aCoach, bCoach) => aCoach.number - bCoach.number)
    .map((r) => r.name)
    .join(", ");
  return (
    <View style={{ borderBottomWidth: 1 }}>
      <View
        style={{
          width: "100%",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          {team?.team?.logo && (
            <Image src={team.team.logo} style={{ maxHeight: 12 }} />
          )}
          <Text
            style={{
              marginLeft: 5,
              marginBottom: "0.5mm",
              fontSize: 10,
              fontFamily: ["Noto Sans", "Noto Sans TC"],
              fontWeight: "bold",
            }}
          >
            {team.team.name}
          </Text>
        </View>
        <Text
          style={{
            marginBottom: "0.5mm",
            fontSize: "8pt",
            fontFamily: ["Noto Sans", "Noto Sans TC"],
            fontWeight: "bold",
          }}
        >
          {`${intl.formatMessage({
            id: "reports.scoresheet.table.coach",
            defaultMessage: "Coach",
          })}: ${coachName}`}
        </Text>
        <Text
          style={{
            marginBottom: "0.5mm",
            fontSize: "8pt",
            fontFamily: ["Noto Sans", "Noto Sans TC"],
            fontWeight: "bold",
          }}
        >
          {`${intl.formatMessage({
            id: "reports.scoresheet.table.coachOrCoachesAssistant",
            defaultMessage: "Assistant Coach(es)",
          })}: ${assistantCoachesNames}`}
        </Text>
      </View>
      <BoxscoreTableHeader {...{ intl, pointsFourAllowed, tableLayout }} />
      {team.personStats &&
        team.personStats.map((r, index) => (
          <BoxScoreReportRow
            row={r}
            index={index}
            key={r.personId}
            intl={intl}
            persons={team.team.persons}
            fixtureProfile={fixtureProfile}
            pointsFourAllowed={pointsFourAllowed}
            tableLayout={tableLayout}
          />
        ))}
      {team.teamStats && team.teamStats.teamOrCoach && (
        <BoxScoreReportRow
          row={team.teamStats.teamOrCoach}
          index={0}
          isTeamCoachRow={true}
          intl={intl}
          fixtureProfile={fixtureProfile}
          pointsFourAllowed={pointsFourAllowed}
          tableLayout={tableLayout}
        />
      )}
      {team.teamStats && team.teamStats.totals && (
        <BoxScoreReportRow
          row={team.teamStats.totals}
          index={1}
          isTotalsRow={true}
          intl={intl}
          teamOrCoachRow={team.teamStats.teamOrCoach}
          fixtureProfile={fixtureProfile}
          pointsFourAllowed={pointsFourAllowed}
          tableLayout={tableLayout}
        />
      )}
    </View>
  );
};

const BoxscoreTableHeader = ({ intl, pointsFourAllowed, tableLayout }) => {
  const { shootingCellWidth, shootingGroupWidth, minutesWidth } = tableLayout;

  const shootingHeaderStyle = {
    width: shootingGroupWidth,
    ...styles.tableHeaderCell,
    ...(pointsFourAllowed ? { fontSize: "6pt" } : {}),
  };

  return (
    <View
      style={{
        ...styles.tableRow,
        backgroundColor: "#f0f0f0",
      }}
    >
    <CellTextBox style={{ ...styles.tableHeaderColumn }}>
      {intl.formatMessage({
        id: "reports.boxscore.table.no",
        defaultMessage: "No",
      })}
    </CellTextBox>
    <CellTextBox style={{ ...styles.tableHeaderColumn, flex: 1 }}>
      {intl.formatMessage({
        id: "reports.boxscore.table.name",
        defaultMessage: "Name",
      })}
    </CellTextBox>
    <CellTextBox style={{ ...styles.tableHeaderColumn, width: minutesWidth }}>
      {intl.formatMessage({
        id: "reports.boxscore.table.min",
        defaultMessage: "Min",
      })}
    </CellTextBox>

    <View style={{ ...styles.tableHeaderGroup }}>
      <View style={{ ...styles.tableHeaderGroupRow }}>
        <CellTextBox
          style={shootingHeaderStyle}
        >
          {intl.formatMessage({
            id: "reports.boxscore.table.fieldgoals",
            defaultMessage: "Field goals",
          })}
        </CellTextBox>
      </View>
      <View style={{ ...styles.tableHeaderGroupRow }}>
        <CellTextBox style={{ width: shootingCellWidth }}>
          {intl.formatMessage({
            id: "reports.boxscore.table.madeattempts",
            defaultMessage: "M/A",
          })}
        </CellTextBox>
        <CellTextBox style={{ width: shootingCellWidth }}>
          {intl.formatMessage({
            id: "reports.boxscore.table.madepercent",
            defaultMessage: "%",
          })}
        </CellTextBox>
      </View>
    </View>

    <View style={{ ...styles.tableHeaderGroup }}>
      <View style={{ ...styles.tableHeaderGroupRow }}>
        <CellTextBox
          style={shootingHeaderStyle}
        >
          {intl.formatMessage({
            id: "reports.boxscore.table.twopoints",
            defaultMessage: "2 Points",
          })}
        </CellTextBox>
      </View>
      <View style={{ ...styles.tableHeaderGroupRow }}>
        <CellTextBox style={{ ...styles.tableHeaderCell, width: shootingCellWidth }}>
          {intl.formatMessage({
            id: "reports.boxscore.table.madeattempts",
            defaultMessage: "M/A",
          })}
        </CellTextBox>
        <CellTextBox style={{ ...styles.tableHeaderCell, width: shootingCellWidth }}>
          {intl.formatMessage({
            id: "reports.boxscore.table.madepercent",
            defaultMessage: "%",
          })}
        </CellTextBox>
      </View>
    </View>

    <View style={{ ...styles.tableHeaderGroup }}>
      <View style={{ ...styles.tableHeaderGroupRow }}>
        <CellTextBox
          style={shootingHeaderStyle}
        >
          {intl.formatMessage({
            id: "reports.boxscore.table.threepoints",
            defaultMessage: "3 Points",
          })}
        </CellTextBox>
      </View>
      <View style={{ ...styles.tableHeaderGroupRow }}>
        <CellTextBox style={{ ...styles.tableHeaderCell, width: shootingCellWidth }}>
          {intl.formatMessage({
            id: "reports.boxscore.table.madeattempts",
            defaultMessage: "M/A",
          })}
        </CellTextBox>
        <CellTextBox style={{ ...styles.tableHeaderCell, width: shootingCellWidth }}>
          {intl.formatMessage({
            id: "reports.boxscore.table.madepercent",
            defaultMessage: "%",
          })}
        </CellTextBox>
      </View>
    </View>

    {pointsFourAllowed && (
      <View style={{ ...styles.tableHeaderGroup }}>
        <View style={{ ...styles.tableHeaderGroupRow }}>
          <CellTextBox
            style={shootingHeaderStyle}
          >
            {intl.formatMessage({
              id: "reports.boxscore.table.fourpoints",
              defaultMessage: "4 Points",
            })}
          </CellTextBox>
        </View>
        <View style={{ ...styles.tableHeaderGroupRow }}>
          <CellTextBox style={{ ...styles.tableHeaderCell, width: shootingCellWidth }}>
            {intl.formatMessage({
              id: "reports.boxscore.table.madeattempts",
              defaultMessage: "M/A",
            })}
          </CellTextBox>
          <CellTextBox style={{ ...styles.tableHeaderCell, width: shootingCellWidth }}>
            {intl.formatMessage({
              id: "reports.boxscore.table.madepercent",
              defaultMessage: "%",
            })}
          </CellTextBox>
        </View>
      </View>
    )}

    <View style={{ ...styles.tableHeaderGroup }}>
      <View style={{ ...styles.tableHeaderGroupRow }}>
        <CellTextBox
          style={shootingHeaderStyle}
        >
          {intl.formatMessage({
            id: "reports.boxscore.table.ft",
            defaultMessage: "Free throws",
          })}
        </CellTextBox>
      </View>
      <View style={{ ...styles.tableHeaderGroupRow }}>
        <CellTextBox style={{ ...styles.tableHeaderCell, width: shootingCellWidth }}>
          {intl.formatMessage({
            id: "reports.boxscore.table.madeattempts",
            defaultMessage: "M/A",
          })}
        </CellTextBox>
        <CellTextBox style={{ ...styles.tableHeaderCell, width: shootingCellWidth }}>
          {intl.formatMessage({
            id: "reports.boxscore.table.madepercent",
            defaultMessage: "%",
          })}
        </CellTextBox>
      </View>
    </View>

    <View style={{ ...styles.tableHeaderGroup }}>
      <View style={{ ...styles.tableHeaderGroupRow }}>
        <CellTextBox
          style={{
            width: "15mm",
            ...styles.tableHeaderCell,
          }}
        >
          {intl.formatMessage({
            id: "reports.boxscore.table.rebounds",
            defaultMessage: "Rebounds",
          })}
        </CellTextBox>
      </View>
      <View style={{ ...styles.tableHeaderGroupRow }}>
        <CellTextBox style={{ ...narrowCells, ...styles.tableHeaderCell }}>
          {intl.formatMessage({
            id: "reports.boxscore.table.reboundsor",
            defaultMessage: "OR",
          })}
        </CellTextBox>
        <CellTextBox style={{ ...narrowCells, ...styles.tableHeaderCell }}>
          {intl.formatMessage({
            id: "reports.boxscore.table.reboundsdr",
            defaultMessage: "DR",
          })}
        </CellTextBox>
        <CellTextBox style={{ ...narrowCells, ...styles.tableHeaderCell }}>
          {intl.formatMessage({
            id: "reports.boxscore.table.reboundstotal",
            defaultMessage: "Tot",
          })}
        </CellTextBox>
      </View>
    </View>

    <CellTextBox
      style={{
        ...styles.tableHeaderColumn,
        ...narrowCells,
        ...styles.tableHeaderCell,
      }}
    >
      {intl.formatMessage({
        id: "reports.boxscore.table.as",
        defaultMessage: "AS",
      })}
    </CellTextBox>
    <CellTextBox
      style={{
        ...styles.tableHeaderColumn,
        ...narrowCells,
        ...styles.tableHeaderCell,
      }}
    >
      {intl.formatMessage({
        id: "reports.boxscore.table.to",
        defaultMessage: "TO",
      })}
    </CellTextBox>
    <CellTextBox
      style={{
        ...styles.tableHeaderColumn,
        ...narrowCells,
        ...styles.tableHeaderCell,
      }}
    >
      {intl.formatMessage({
        id: "reports.boxscore.table.st",
        defaultMessage: "ST",
      })}
    </CellTextBox>
    <CellTextBox
      style={{
        ...styles.tableHeaderColumn,
        ...narrowCells,
        ...styles.tableHeaderCell,
      }}
    >
      BS
    </CellTextBox>

    <View style={{ ...styles.tableHeaderGroup }}>
      <View style={{ ...styles.tableHeaderGroupRow }}>
        <CellTextBox
          style={{
            width: "10mm",
            ...styles.tableHeaderCell,
          }}
        >
          {intl.formatMessage({
            id: "reports.boxscore.table.fouls",
            defaultMessage: "Fouls",
          })}
        </CellTextBox>
      </View>
      <View style={{ ...styles.tableHeaderGroupRow }}>
        <CellTextBox style={{ ...narrowCells, ...styles.tableHeaderCell }}>
          {intl.formatMessage({
            id: "reports.boxscore.table.foulspf",
            defaultMessage: "PF",
          })}
        </CellTextBox>
        <CellTextBox style={{ ...narrowCells, ...styles.tableHeaderCell }}>
          {intl.formatMessage({
            id: "reports.boxscore.table.foulsfd",
            defaultMessage: "FD",
          })}
        </CellTextBox>
      </View>
    </View>

    <CellTextBox
      style={{
        ...styles.tableHeaderColumn,
        ...narrowCells,
        ...styles.tableHeaderCell,
      }}
    >
      {intl.formatMessage({
        id: "reports.boxscore.table.plusminus",
        defaultMessage: "+/-",
      })}
    </CellTextBox>
    <CellTextBox
      style={{
        ...styles.tableHeaderColumn,
        ...narrowCells,
        ...styles.tableHeaderCell,
      }}
    >
      {intl.formatMessage({
        id: "reports.boxscore.table.ef",
        defaultMessage: "EF",
      })}
    </CellTextBox>
    <CellTextBox
      style={{ ...styles.tableHeaderColumn, ...styles.tableHeaderCell }}
    >
      {intl.formatMessage({
        id: "reports.boxscore.table.pts",
        defaultMessage: "PTS",
      })}
    </CellTextBox>
    </View>
  );
};

const BoxScoreReportRow = ({
  row,
  index,
  isTeamCoachRow = false,
  isTotalsRow = false,
  intl,
  teamOrCoachRow,
  persons,
  fixtureProfile,
  pointsFourAllowed,
  tableLayout,
}) => {
  const { shootingCellWidth, shootingColumnsWidth, minutesWidth } = tableLayout;
  let rowMinutesText = "",
    parsedMinutes = {},
    dnp = false;

  if (!isTeamCoachRow) {
    parsedMinutes = parseISO8601(row.minutes);
    rowMinutesText =
      parsedMinutes.minutes.toString() +
      (isTotalsRow
        ? ""
        : ":" + NumberAsTwoDigits({ value: parsedMinutes.seconds }));
    if (parsedMinutes.minutes === 0 && parsedMinutes.seconds === 0) {
      dnp = true;
      rowMinutesText = intl.formatMessage({
        id: "reports.boxscore.table.dnp",
        defaultMessage: "DNP",
      });
    }
  }

  const printEfficiency = () => {
    if (fixtureProfile.efficiencyType === "EFFICIENCY")
      return safeZero(row, "efficiency");
    if (fixtureProfile.efficiencyType === "INDEX_OF_SUCCESS")
      return safeZero(row, "indexOfSuccess");
    if (fixtureProfile.efficiencyType === "PERFORMANCE_INDEX_RATING")
      return safeZero(row, "pir");
    // in case there's no efficiencyType for the match profile, lets default to something
    return safeZero(row, "efficiency");
  };

  return (
    <View
      style={{
        ...styles.tableRow,
        backgroundColor: index % 2 ? "#f0f0f0" : "white",
      }}
    >
      {isTeamCoachRow && (
        <React.Fragment>
          <CellTextBox style={{ flex: 1, textAlign: "left", paddingLeft: 2 }}>
            {intl.formatMessage({
              id: "reports.boxscore.table.teamcoach",
              defaultMessage: "Team/Coach",
            })}
          </CellTextBox>
          <CellTextBox style={{ width: shootingColumnsWidth }}> </CellTextBox>
          <CellTextBox style={narrowCells}>
            {safeZero(row, "reboundsTeamOffensive")}
          </CellTextBox>
          <CellTextBox style={narrowCells}>
            {safeZero(row, "reboundsTeamDefensive")}
          </CellTextBox>
          <CellTextBox style={narrowCells}>
            {safeZero(row, "reboundsTeamTotal")}
          </CellTextBox>

          <CellTextBox style={narrowCells}> </CellTextBox>
          <CellTextBox style={narrowCells}>
            {safeZero(row, "turnoversTeam")}
          </CellTextBox>
          <CellTextBox style={{ width: "10mm" }}> </CellTextBox>
          <CellTextBox style={narrowCells}>
            {safeZero(row, "fouls")}
          </CellTextBox>
          <CellTextBox style={{ width: "23mm" }}> </CellTextBox>
        </React.Fragment>
      )}
      {!isTeamCoachRow && (
        <React.Fragment>
          {!isTotalsRow && (
            <React.Fragment>
              <CellTextBox>{row.bib ?? " "}</CellTextBox>
              <CellTextBox
                style={{ flex: 1, textAlign: "left", paddingLeft: 2 }}
              >
                {`${row.starter ? "* " : ""}${safeString(
                  row,
                  "personFullName"
                )}${
                  persons[row.personId]?.captain
                    ? ` ${intl.formatMessage({
                        id: "reports.boxscore.table.captain",
                        defaultMessage: "(C)",
                      })}`
                    : ""
                }`}
              </CellTextBox>
            </React.Fragment>
          )}
          {isTotalsRow && (
            <React.Fragment>
              <CellTextBox
                style={{ flex: 1, textAlign: "left", paddingLeft: 2 }}
              >
                {intl.formatMessage({
                  id: "reports.boxscore.table.totals",
                  defaultMessage: "Totals",
                })}
              </CellTextBox>
            </React.Fragment>
          )}
          <CellTextBox style={{ width: minutesWidth }}>{rowMinutesText}</CellTextBox>
          <CellTextBox style={{ width: shootingCellWidth }}>
            {!dnp &&
              safeZero(row, "fieldGoalsMade") +
                "/" +
                safeZero(row, "fieldGoalsAttempted")}
          </CellTextBox>
          <CellTextBox style={{ width: shootingCellWidth }}>
            {!dnp && (
              <NumberAsPercent
                percentValue={safeZero(row, "fieldGoalsPercentage")}
              />
            )}
          </CellTextBox>
          <CellTextBox style={{ width: shootingCellWidth }}>
            {!dnp &&
              safeZero(row, "pointsTwoMade") +
                "/" +
                safeZero(row, "pointsTwoAttempted")}
          </CellTextBox>
          <CellTextBox style={{ width: shootingCellWidth }}>
            {!dnp && (
              <NumberAsPercent
                percentValue={safeZero(row, "pointsTwoPercentage")}
              />
            )}
          </CellTextBox>
          <CellTextBox style={{ width: shootingCellWidth }}>
            {!dnp &&
              safeZero(row, "pointsThreeMade") +
                "/" +
                safeZero(row, "pointsThreeAttempted")}
          </CellTextBox>
          <CellTextBox style={{ width: shootingCellWidth }}>
            {!dnp && (
              <NumberAsPercent
                percentValue={safeZero(row, "pointsThreePercentage")}
              />
            )}
          </CellTextBox>

          {pointsFourAllowed && (
            <React.Fragment>
              <CellTextBox style={{ width: shootingCellWidth }}>
                {!dnp &&
                  safeZero(row, "pointsFourMade") +
                    "/" +
                    safeZero(row, "pointsFourAttempted")}
              </CellTextBox>
              <CellTextBox style={{ width: shootingCellWidth }}>
                {!dnp && (
                  <NumberAsPercent percentValue={safeZero(row, "pointsFourPercentage")} />
                )}
              </CellTextBox>
            </React.Fragment>
          )}

          <CellTextBox style={{ width: shootingCellWidth }}>
            {!dnp &&
              safeZero(row, "freeThrowsMade") +
                "/" +
                safeZero(row, "freeThrowsAttempted")}
          </CellTextBox>
          <CellTextBox style={{ width: shootingCellWidth }}>
            {!dnp && (
              <NumberAsPercent
                percentValue={safeZero(row, "freeThrowsPercentage")}
              />
            )}
          </CellTextBox>

          <CellTextBox style={narrowCells}>
            {!dnp && safeZero(row, "reboundsOffensive")}
          </CellTextBox>
          <CellTextBox style={narrowCells}>
            {!dnp && safeZero(row, "reboundsDefensive")}
          </CellTextBox>
          <CellTextBox style={narrowCells}>
            {!dnp && safeZero(row, "rebounds")}
          </CellTextBox>

          <CellTextBox style={narrowCells}>
            {!dnp && safeZero(row, "assists")}
          </CellTextBox>
          <CellTextBox style={narrowCells}>
            {!dnp && safeZero(row, "turnovers")}
          </CellTextBox>
          <CellTextBox style={narrowCells}>
            {!dnp && safeZero(row, "steals")}
          </CellTextBox>
          <CellTextBox style={narrowCells}>
            {!dnp && safeZero(row, "blocks")}
          </CellTextBox>
          <CellTextBox style={narrowCells}>
            {teamOrCoachRow
              ? parseInt(safeZero(row, "foulsTotal"), 10) +
                parseInt(safeZero(teamOrCoachRow, "fouls"), 10)
              : safeZero(row, "foulsTotal")}
          </CellTextBox>
          <CellTextBox style={narrowCells}>
            {!dnp && safeZero(row, "foulsDrawn")}
          </CellTextBox>
          <CellTextBox style={narrowCells}>
            {!dnp && safeZero(row, "plusMinus")}
          </CellTextBox>
          <CellTextBox style={narrowCells}>
            {!dnp && printEfficiency()}
          </CellTextBox>
          <CellTextBox>{!dnp && safeZero(row, "points")}</CellTextBox>
        </React.Fragment>
      )}
    </View>
  );
};

const SideTableRow = (props) => {
  const { labelText, homeTeamText, awayTeamText, rowStyle } = props;
  const rowHeight = "4mm";
  return (
    <View style={{ ...styles.tableRow, ...rowStyle, height: rowHeight }}>
      <CellTextBox style={{ width: "40mm", height: rowHeight }}>
        {labelText}
      </CellTextBox>
      <CellTextBox style={{ flex: 1, height: rowHeight }}>
        {homeTeamText}
      </CellTextBox>
      {awayTeamText && (
        <CellTextBox style={{ flex: 1, height: rowHeight }}>
          {awayTeamText}
        </CellTextBox>
      )}
    </View>
  );
};

const SideTableLeft = ({ homeTeam, awayTeam, intl }) => {
  const defaults = {
    pointsFromTurnover: 0,
    pointsInThePaint: 0,
    pointsSecondChance: 0,
    timesScoreLevel: 0,
    pointsFastBreak: 0,
    pointsFromBench: 0,
  };
  const homeTeamStats = homeTeam.teamStats ?? defaults;
  const awayTeamStats = awayTeam.teamStats ?? defaults;

  return (
    <View
      style={{ flexDirection: "column", width: "95mm", borderBottomWidth: 1 }}
    >
      <SideTableRow
        rowStyle={{ backgroundColor: "#f0f0f0" }}
        labelText={" "}
        homeTeamText={homeTeam.team.name}
        awayTeamText={awayTeam.team.name}
      />

      <SideTableRow
        labelText={intl.formatMessage({
          id: "reports.boxscore.table.pointsfromto",
          defaultMessage: "Points from Turnovers",
        })}
        homeTeamText={safeZero(homeTeamStats, "pointsFromTurnover")}
        awayTeamText={safeZero(awayTeamStats, "pointsFromTurnover")}
      />
      <SideTableRow
        labelText={intl.formatMessage({
          id: "reports.boxscore.table.pointsinpaint",
          defaultMessage: "Points in the Paint",
        })}
        homeTeamText={safeZero(homeTeamStats, "pointsInThePaint")}
        awayTeamText={safeZero(awayTeamStats, "pointsInThePaint")}
      />
      <SideTableRow
        labelText={intl.formatMessage({
          id: "reports.boxscore.table.pointssecondchance",
          defaultMessage: "Second Chance Points",
        })}
        homeTeamText={safeZero(homeTeamStats, "pointsSecondChance")}
        awayTeamText={safeZero(awayTeamStats, "pointsSecondChance")}
      />
      <SideTableRow
        labelText={intl.formatMessage({
          id: "reports.boxscore.table.pointsfastbreak",
          defaultMessage: "Fast Break Points",
        })}
        homeTeamText={safeZero(homeTeamStats, "pointsFastBreak")}
        awayTeamText={safeZero(awayTeamStats, "pointsFastBreak")}
      />
      <SideTableRow
        labelText={intl.formatMessage({
          id: "reports.boxscore.table.pointsfromto",
          defaultMessage: "Bench Points",
        })}
        homeTeamText={safeZero(homeTeamStats, "pointsFromBench")}
        awayTeamText={safeZero(awayTeamStats, "pointsFromBench")}
      />
    </View>
  );
};

const SideTableRight = ({ homeTeam, awayTeam, intl }) => {
  const defaults = {
    biggestLead: 0,
    biggestScoringRun: 0,
    leadChanges: 0,
    timesScoreLevel: 0,
    timeInLead: "0",
  };
  const homeTeamStats = homeTeam.teamStats ?? defaults;
  const awayTeamStats = awayTeam.teamStats ?? defaults;

  return (
    <View
      style={{ flexDirection: "column", width: "95mm", borderBottomWidth: 1 }}
    >
      <SideTableRow
        rowStyle={{ backgroundColor: "#f0f0f0" }}
        labelText={" "}
        homeTeamText={homeTeam.team.name}
        awayTeamText={awayTeam.team.name}
      />

      <SideTableRow
        labelText={intl.formatMessage({
          id: "reports.boxscore.table.biggestlead",
          defaultMessage: "Biggest Lead",
        })}
        homeTeamText={safeZero(homeTeamStats, "biggestLead")}
        awayTeamText={safeZero(awayTeamStats, "biggestLead")}
      />

      <SideTableRow
        labelText={intl.formatMessage({
          id: "reports.boxscore.table.biggestrun",
          defaultMessage: "Biggest Scoring Run",
        })}
        homeTeamText={safeZero(homeTeamStats, "biggestScoringRun")}
        awayTeamText={safeZero(awayTeamStats, "biggestScoringRun")}
      />
      <SideTableRow
        labelText={intl.formatMessage({
          id: "reports.boxscore.table.leadchanges",
          defaultMessage: "Lead Changes",
        })}
        homeTeamText={safeZero(homeTeamStats, "leadChanges")}
      />

      <SideTableRow
        labelText={intl.formatMessage({
          id: "reports.boxscore.table.timestied",
          defaultMessage: "Times Tied",
        })}
        homeTeamText={safeZero(homeTeamStats, "timesScoreLevel")}
      />

      <SideTableRow
        labelText={intl.formatMessage({
          id: "reports.boxscore.table.timewithlead",
          defaultMessage: "Time with Lead",
        })}
        homeTeamText={`${
          parseISO8601(homeTeamStats.timeInLead ?? "0").minutes
        }:${NumberAsTwoDigits({
          value: parseISO8601(homeTeamStats.timeInLead ?? "0").seconds,
        })}`}
        awayTeamText={`${
          parseISO8601(awayTeamStats.timeInLead ?? "0").minutes
        }:${NumberAsTwoDigits({
          value: parseISO8601(awayTeamStats.timeInLead ?? "0").seconds,
        })}`}
      />
    </View>
  );
};
